import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ArrowRight, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

// Timestamps come as ISO strings from backend
function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function AlertsBell() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: list }, { data: count }] = await Promise.all([
        api.get("/alerts"),
        api.get("/alerts/unread-count"),
      ]);
      setAlerts(list.slice(0, 20));
      setUnread(count.count);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  const markRead = async (id) => {
    try {
      await api.post(`/alerts/${id}/read`);
      load();
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/alerts/read-all");
      toast.success("All alerts marked read");
      load();
    } catch {
      /* ignore */
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="alerts-bell-button"
          className="relative w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
          aria-label="Alerts"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unread > 0 && (
            <span
              data-testid="alerts-unread-badge"
              className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="alerts-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="font-bold text-slate-900">Scan alerts</div>
            <div className="text-xs text-slate-500">
              {unread > 0 ? `${unread} unread` : "All caught up"}
            </div>
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700"
              data-testid="alerts-mark-all-read-btn"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-10 px-4">
              No alerts yet.
              <br />
              <span className="text-xs">
                Enable scan alerts on a campaign to be notified when scans spike.
              </span>
            </div>
          ) : (
            <ul>
              {alerts.map((a) => (
                <li
                  key={a.id}
                  data-testid={`alert-item-${a.id}`}
                  className={`px-4 py-3 border-b border-slate-100 last:border-0 flex items-start gap-3 ${
                    a.read ? "bg-white" : "bg-blue-50/40"
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 shrink-0">
                    🚀
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {a.campaign_name}
                    </div>
                    <div className="text-xs text-slate-600">
                      <b>{a.scan_count}</b> scans in {a.window_hours}h (threshold {a.threshold})
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {timeAgo(a.triggered_at)}
                    </div>
                  </div>
                  {!a.read && (
                    <button
                      onClick={() => markRead(a.id)}
                      className="text-slate-400 hover:text-emerald-600"
                      title="Mark read"
                      data-testid={`alert-mark-read-${a.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-200 p-2">
          <Link to="/pricing" onClick={() => setOpen(false)}>
            <Button variant="ghost" size="sm" className="w-full justify-between text-xs">
              Alerts are a Pro feature — upgrade
              <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
